import {
  createContext,
  useCallback,
  useContext,
  useId,
  useMemo,
  useRef,
  type KeyboardEvent,
  type ReactNode,
} from 'react'
import { cn } from '@/shared/lib/utils'
import { Check } from 'lucide-react'

// ─────────────────────────────────────────────────────────────────────────────
// Context - links each <RadioCard> to its parent <RadioCardGroup>.
// We carry the value as `unknown` here; the generic on <RadioCardGroup>
// re-tightens the type for consumers without polluting the context type.
// ─────────────────────────────────────────────────────────────────────────────

interface RadioCardGroupContextValue {
  name: string
  value: unknown
  onChange: (next: unknown) => void
  disabled?: boolean
  registerItem: (value: unknown, ref: HTMLButtonElement | null) => void
  focusSibling: (fromValue: unknown, dir: 1 | -1) => void
}

const RadioCardGroupContext = createContext<RadioCardGroupContextValue | null>(null)

// ─────────────────────────────────────────────────────────────────────────────
// <RadioCardGroup>
// ─────────────────────────────────────────────────────────────────────────────

export interface RadioCardGroupProps<TValue> {
  value: TValue
  onChange: (next: TValue) => void
  /** Accessible label for the radiogroup. */
  ariaLabel?: string
  /** ID of an element labelling the group (use instead of ariaLabel). */
  ariaLabelledBy?: string
  /** Disables every child card. Individual cards can still set disabled themselves. */
  disabled?: boolean
  /** Tailwind classes for the wrapper. Defaults to a responsive grid. */
  className?: string
  children: ReactNode
}

export function RadioCardGroup<TValue>({
  value,
  onChange,
  ariaLabel,
  ariaLabelledBy,
  disabled,
  className,
  children,
}: RadioCardGroupProps<TValue>) {
  const name = useId()
  // Insertion-ordered map so arrow-key navigation follows DOM order.
  const itemsRef = useRef<Map<unknown, HTMLButtonElement>>(new Map())

  const registerItem = useCallback((itemValue: unknown, ref: HTMLButtonElement | null) => {
    const map = itemsRef.current
    if (ref) map.set(itemValue, ref)
    else map.delete(itemValue)
  }, [])

  // WAI-ARIA radio group: an arrow key moves focus AND the selection.
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange
  const focusSibling = useCallback((fromValue: unknown, dir: 1 | -1) => {
    const entries = Array.from(itemsRef.current.entries()).filter(([, el]) => !el.disabled)
    if (entries.length === 0) return
    const idx = entries.findIndex(([v]) => Object.is(v, fromValue))
    const nextIdx = idx === -1 ? 0 : (idx + dir + entries.length) % entries.length
    entries[nextIdx][1].focus()
    onChangeRef.current(entries[nextIdx][0] as TValue)
  }, [])

  const ctx = useMemo<RadioCardGroupContextValue>(
    () => ({
      name,
      value,
      onChange: (next) => onChange(next as TValue),
      disabled,
      registerItem,
      focusSibling,
    }),
    [name, value, onChange, disabled, registerItem, focusSibling],
  )

  return (
    <RadioCardGroupContext.Provider value={ctx}>
      <div
        role="radiogroup"
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        className={cn('grid gap-3 sm:grid-cols-2', className)}
      >
        {children}
      </div>
    </RadioCardGroupContext.Provider>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// <RadioCard>
//
// Two usage modes:
//   1. Inside <RadioCardGroup>: `value` is required; selection is wired up
//      automatically (and aria/keyboard nav is provided).
//   2. Standalone: pass `selected` + `onSelect` to drive it yourself. Useful
//      where the parent has selection semantics that aren't a pure radio
//      (e.g. multi-select cards in <ModifierPickerModal>).
// ─────────────────────────────────────────────────────────────────────────────

interface RadioCardCommonProps {
  title: ReactNode
  description?: ReactNode
  icon?: ReactNode
  badge?: ReactNode
  /** Right-aligned content rendered on the same row as the title (e.g. a price). */
  trailing?: ReactNode
  disabled?: boolean
  className?: string
  /** Optional richer body rendered below the title row. */
  children?: ReactNode
}

interface RadioCardGroupedProps<TValue> extends RadioCardCommonProps {
  value: TValue
  selected?: never
  onSelect?: never
}

interface RadioCardStandaloneProps extends RadioCardCommonProps {
  selected: boolean
  onSelect: () => void
  value?: never
}

export type RadioCardProps<TValue = unknown> =
  | RadioCardGroupedProps<TValue>
  | RadioCardStandaloneProps

export function RadioCard<TValue = unknown>(props: RadioCardProps<TValue>) {
  const { title, description, icon, badge, trailing, disabled, className, children } = props
  const group = useContext(RadioCardGroupContext)
  const buttonRef = useRef<HTMLButtonElement | null>(null)

  const isGrouped = 'value' in props && props.value !== undefined
  const selected = isGrouped
    ? Object.is(group?.value, (props as RadioCardGroupedProps<TValue>).value)
    : (props as RadioCardStandaloneProps).selected

  const isDisabled = !!disabled || (isGrouped && !!group?.disabled)

  // Register with the group so arrow-key nav can find this card.
  const setRef = useCallback(
    (el: HTMLButtonElement | null) => {
      buttonRef.current = el
      if (isGrouped && group) {
        group.registerItem((props as RadioCardGroupedProps<TValue>).value, el)
      }
    },
    // We intentionally include `props.value` via the cast so unmount cleans up.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [group, isGrouped, isGrouped ? (props as RadioCardGroupedProps<TValue>).value : undefined],
  )

  const handleActivate = () => {
    if (isDisabled) return
    if (isGrouped && group) {
      group.onChange((props as RadioCardGroupedProps<TValue>).value)
    } else if (!isGrouped) {
      ;(props as RadioCardStandaloneProps).onSelect()
    }
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (isDisabled) return
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault()
      handleActivate()
      return
    }
    if (!isGrouped || !group) return
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault()
      group.focusSibling((props as RadioCardGroupedProps<TValue>).value, 1)
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault()
      group.focusSibling((props as RadioCardGroupedProps<TValue>).value, -1)
    }
  }

  // Roving tabindex: within a group, only the selected card is tabbable.
  // Standalone cards remain tabbable normally.
  // With nothing chosen yet every card stays reachable, or the group would
  // have no tab stop at all.
  const tabIndex = isGrouped ? (selected || group?.value == null ? 0 : -1) : 0

  return (
    <button
      ref={setRef}
      type="button"
      role="radio"
      aria-checked={selected}
      aria-disabled={isDisabled || undefined}
      disabled={isDisabled}
      tabIndex={tabIndex}
      onClick={handleActivate}
      onKeyDown={handleKeyDown}
      className={cn(
        'flex h-full items-start gap-3 rounded-2xl border p-4 text-left transition-colors',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange focus-visible:ring-offset-1',
        selected
          ? 'border-brand-orange bg-brand-orange/5 shadow-sm'
          : 'border-gray-200 bg-white hover:border-gray-300',
        isDisabled && 'cursor-not-allowed opacity-50 hover:border-gray-200',
        className,
      )}
    >
      {/* Q5-BREST8 / Q6-REST7: a purely visual mark. A real checkbox input
          here was a second, unnamed tab stop inside every card, so
          Tab-Tab-Enter landed on Cancel instead of Add. */}
      <span
        aria-hidden="true"
        className={cn(
          'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-all',
          selected
            ? 'border-brand-orange bg-brand-orange text-white'
            : 'border-gray-200 bg-transparent text-transparent',
        )}
      >
        <Check size={12} strokeWidth={4} />
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        {icon && (
          <span
            className={cn(
              'flex h-9 w-9 items-center justify-center rounded-xl',
              selected ? 'bg-brand-orange text-white' : 'bg-gray-100 text-gray-500',
            )}
          >
            {icon}
          </span>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm font-semibold text-brand-rail">{title}</p>
          {badge}
          {trailing && <span className="ml-auto shrink-0">{trailing}</span>}
        </div>
        {description && <p className="text-[11px] text-gray-500">{description}</p>}
        {children}
      </div>
    </button>
  )
}
